/**
 * Alert Component
 */

import { IBuilderComponent } from '../../base'
import {
  formatFromVariableText,
  formatResponsiveValue,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import { BaseAiComponentInput } from '../../registry'
import type {
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import alertTemplate from './alert.template.json'

export interface IAlertComponentSettings {
  // Data settings
  'alert-data-title': VariableTextValue
  'alert-data-description': VariableTextValue
  'alert-data-icon': any

  // Design settings
  'alert-design-color-variant': string
  'alert-design-scheme': any
  'alert-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'alert-design-alignment': ResponsiveValue<any>
  'alert-design-typography': {
    'alert-design-title-typography': ResponsiveValue<TypographyValue>
    'alert-design-description-typography': ResponsiveValue<TypographyValue>
  }
  'alert-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'alert-design-padding': ResponsiveValue<SpacingInputValue>
  'alert-design-outline': any
  'alert-design-elevation': boolean
}

export interface IAlertComponent extends IBuilderComponent {
  componentId: 'alert'
  groupId: 'app'
  settings: IAlertComponentSettings
}

export interface AiAlertInput extends BaseAiComponentInput {
  alertTitle?: string[] // Format: ["text:Alert Title"] or ["text:Warning: ", "cr:message"]
  description?: string
  icon?: string // Icon name or URL
  colorVariant?: string // e.g., "primary-30", "error-40"
  width?: string // Format: "w:100%" or "w:600px" or "w:100% de:w:600px"
  margin?: string // Format: "m:10px" or "mt:10px mb:10px"
  padding?: string // Format: "p:16px"
  alignment?: string // Format: "center" or "left ta:center de:right"
  typography?: string // Format: "body-medium" or "body-medium ta:body-large"
  elevation?: boolean // Shadow elevation
}

const ALERT_DEFAULT_TEMPLATE = alertTemplate as unknown as IAlertComponentSettings

export class AlertComp {
  getTemplate(): IAlertComponentSettings {
    return ALERT_DEFAULT_TEMPLATE
  }

  /**
   * format alert component settings to AI alert input
   */
  format(settings: IAlertComponentSettings): AiAlertInput {
    const result: AiAlertInput = {
      type: 'alert',
    }

    // Data - Title
    if (settings['alert-data-title']) {
      result.alertTitle = formatFromVariableText(settings['alert-data-title'])
    }

    // Data - Description
    if (settings['alert-data-description']) {
      const desc = formatFromVariableText(settings['alert-data-description'])
      result.description = desc.length > 0 ? desc.join(' ') : undefined
    }

    // Data - Icon
    if (settings['alert-data-icon']) {
      result.icon = settings['alert-data-icon']
    }

    // Design - Color Variant
    if (settings['alert-design-color-variant']) {
      result.colorVariant = settings['alert-design-color-variant']
    }

    // Design - Width (handle OverflowSettings)
    if (settings['alert-design-width']) {
      const width = formatWidthFormat(settings['alert-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Margin (handle OverflowSettings)
    if (settings['alert-design-margin']) {
      const margin = formatSpacingFormat(settings['alert-design-margin'])
      if (margin) {
        result.margin = margin
      }
    }

    // Design - Padding
    if (settings['alert-design-padding']) {
      result.padding = formatSpacingFormat(settings['alert-design-padding'])
    }

    // Design - Alignment
    if (settings['alert-design-alignment']) {
      result.alignment = formatResponsiveValue(settings['alert-design-alignment'], (val) => String(val))
    }

    // Design - Typography (nested structure)
    if (settings['alert-design-typography']) {
      const titleTypo = formatTypographyFormat(settings['alert-design-typography']['alert-design-title-typography'])
      const descTypo = formatTypographyFormat(
        settings['alert-design-typography']['alert-design-description-typography']
      )
      // If both are the same, use single typography field
      if (titleTypo === descTypo) {
        result.typography = titleTypo
      } else {
        // For now, use title typography as default
        result.typography = titleTypo
      }
    }

    // Design - Elevation
    if (settings['alert-design-elevation'] !== undefined) {
      result.elevation = settings['alert-design-elevation']
    }

    return result
  }

  /**
   * parse AI alert input to alert component settings
   */
  parse(input: AiAlertInput): IAlertComponentSettings {
    const result = deepMerge(ALERT_DEFAULT_TEMPLATE, {})

    // Data - Title
    if (input.alertTitle) {
      result['alert-data-title'] = parseToVariableText(input.alertTitle)
    }

    // Data - Description
    if (input.description) {
      result['alert-data-description'] = parseToVariableText(input.description)
    }

    // Data - Icon (simple string value)
    if (input.icon) {
      result['alert-data-icon'] = input.icon
    }

    // Design - Color Variant
    if (input.colorVariant) {
      result['alert-design-color-variant'] = input.colorVariant
    }

    // Design - Width
    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['alert-design-width'] = {
        ...widthParsed,
        overflowSetting: result['alert-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      const templateMargin = result['alert-design-margin']

      // Merge parsed values with template defaults for each breakpoint
      const breakpoints = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv'] as const
      breakpoints.forEach((breakpoint) => {
        if (marginParsed[breakpoint]) {
          result['alert-design-margin'][breakpoint] = {
            ...templateMargin[breakpoint],
            ...marginParsed[breakpoint],
          }
        }
      })
    }

    // Design - Padding
    if (input.padding) {
      result['alert-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Alignment
    if (input.alignment) {
      result['alert-design-alignment'] = parseResponsiveFormat(input.alignment, (v) => v)
    }

    // Design - Typography (nested structure)
    if (input.typography) {
      const parsedTypography = parseTypographyFormat(input.typography)
      result['alert-design-typography']['alert-design-title-typography'] = parsedTypography
      result['alert-design-typography']['alert-design-description-typography'] = parsedTypography
    }

    // Design - Elevation
    if (input.elevation !== undefined && input.elevation !== null) {
      result['alert-design-elevation'] = input.elevation
    }

    return result
  }
}
